// src/components/profiles/modals/CreateProfileModal.tsx
// 🔧 MODAL CRÉATION PROFIL ENFANT ULTRA PREMIUM - VERSION FINALE CORRIGÉE

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../hooks/useToast';
import Avatar from '../../ui/Avatar';
import {
  User,
  Calendar,
  Palette,
  Shirt,
  Camera,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Upload,
  Baby,
  Heart,
  Star,
  Sparkles,
  Gift,
} from 'lucide-react';

import AutocompletePreferences from '../../ui/AutocompletePreferences';
import { API_BASE_URL } from '../../../config/api';

interface CreateProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    birthDate: string;
    gender: 'MALE' | 'FEMALE' | 'UNISEX';
    avatar_url?: string;
    clothingSizes?: string[];
    shoeSizes?: string[];
    preferredBrands?: string[];
    favoriteColors?: string[];
    preferredStyles?: string[];
  }) => void;
  isLoading: boolean;
  /** Mode onboarding parent : retire la contrainte d'âge enfant sur la date */
  isParentOnboarding?: boolean;
}

// ============================================================================
// 🎯 TAILLES ENFANTS COHÉRENTES ET COMPLÈTES
// ============================================================================

// ✅ VÊTEMENTS ENFANTS LOGIQUES : Mois puis Années
const ALL_CHILD_CLOTHING_SIZES = [
  // Bébés (mois) - Progression logique
  '1M',
  '3M',
  '6M',
  '9M',
  '12M',
  '18M',
  '24M',
  '36M',
  // Enfants (années) - Progression logique
  '1A',
  '2A',
  '3A',
  '4A',
  '5A',
  '6A',
  '7A',
  '8A',
  '9A',
  '10A',
  '11A',
  '12A',
  '13A',
  '14A',
  '15A',
  '16A',
];

// ✅ POINTURES ENFANTS COMPLÈTES (UNIQUEMENT EU)
const ALL_CHILD_SHOE_SIZES = [
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
  '24',
  '25',
  '26',
  '27',
  '28',
  '29',
  '30',
  '31',
  '32',
  '33',
  '34',
  '35',
  '36',
  '37',
  '38',
  '39',
  '40',
  '41',
  '42',
  '43',
  '44',
  '45',
  '46',
  '47',
  '48',
  '49',
  '50',
];

// Tailles adultes (si besoin)
const ADULT_CLOTHING_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const ADULT_SHOE_SIZES = [
  '36',
  '37',
  '38',
  '39',
  '40',
  '41',
  '42',
  '43',
  '44',
  '45',
  '46',
  '47',
  '48',
  '49',
  '50',
];

// ============================================================================
// 🎨 COMPOSANT SELECT PREMIUM ULTRA DESIGN
// ============================================================================

interface PremiumSelectProps {
  value: string;
  onSelect: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

const PremiumSelect: React.FC<PremiumSelectProps> = ({
  value,
  onSelect,
  options,
  placeholder,
  disabled,
  icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>(
    'bottom'
  );

  const selectedOption = options.find(opt => opt.value === value);

  // ✅ CALCUL INTELLIGENT DE LA POSITION DU DROPDOWN
  const handleToggle = (e: React.MouseEvent) => {
    if (disabled) return;

    const buttonRect = e.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    // Si pas assez de place en bas (moins de 250px), ouvrir vers le haut
    if (spaceBelow < 250 && spaceAbove > spaceBelow) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }

    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full p-2.5 sm:p-3 md:p-4 border rounded-lg sm:rounded-xl text-sm sm:text-base font-medium transition-all duration-200 hover:scale-[1.02] text-left flex items-center justify-between"
        style={{
          borderColor: isOpen ? 'var(--primary)' : '#e5e7eb',
          backgroundColor: 'white',
          boxShadow: isOpen ? '0 0 0 2px rgba(200, 168, 130, 0.1)' : 'none',
        }}
        disabled={disabled}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span
            className={selectedOption ? 'text-brand-dark' : 'text-brand-warm'}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronRight
          className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
          style={{ color: 'var(--brand-500)' }}
        />
      </button>

      {isOpen && (
        <>
          {/* Overlay pour fermer */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* ✅ Dropdown premium avec position intelligente */}
          <div
            className={`absolute left-0 right-0 mt-1 bg-white border rounded-lg sm:rounded-xl shadow-2xl z-20 max-h-48 sm:max-h-60 overflow-y-auto ${
              dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
            style={{
              borderColor: '#e5e7eb',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            }}
          >
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onSelect(option.value);
                  setIsOpen(false);
                }}
                className="w-full p-2.5 sm:p-3 text-left text-sm sm:text-base font-medium transition-all duration-150 hover:scale-[1.02] first:rounded-t-lg first:sm:rounded-t-xl last:rounded-b-lg last:sm:rounded-b-xl"
                style={{
                  backgroundColor: value === option.value ? '#f3f4f6' : 'white',
                  color:
                    value === option.value ? 'var(--brand-500)' : '#374151',
                }}
                onMouseEnter={e => {
                  if (value !== option.value) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseLeave={e => {
                  if (value !== option.value) {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                <div className="flex items-center gap-2">
                  {value === option.value && (
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                  )}
                  <span
                    className={value === option.value ? 'font-semibold' : ''}
                  >
                    {option.label}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// 🔧 FONCTION CALCUL D'ÂGE
// ============================================================================

const calculateChildAge = (
  birthDate: string
): { ageValue: number; ageText: string; displayText: string } => {
  const today = new Date();
  const birth = new Date(birthDate);
  let totalMonths = (today.getFullYear() - birth.getFullYear()) * 12;
  totalMonths += today.getMonth() - birth.getMonth();
  if (today.getDate() < birth.getDate()) totalMonths--;

  if (totalMonths <= 36) {
    return {
      ageValue: totalMonths,
      ageText: `${totalMonths} mois`,
      displayText: `${totalMonths} mois • Profil enfant`,
    };
  } else {
    const years = Math.floor(totalMonths / 12);
    const remainingMonths = totalMonths % 12;
    let ageText = `${years} ${years === 1 ? 'an' : 'ans'}`;
    if (remainingMonths > 0) ageText += ` et ${remainingMonths} mois`;
    return {
      ageValue: years,
      ageText: ageText,
      displayText: `${ageText} • Profil enfant`,
    };
  }
};

// ============================================================================
// 🎨 COMPOSANT PRINCIPAL ULTRA PREMIUM
// ============================================================================

const CreateProfileModal: React.FC<CreateProfileModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  isParentOnboarding = false,
}) => {
  const { showError } = useToast();
  const { t } = useTranslation([
    'profiles-children',
    'preferences-common',
    'sizes',
    'common',
  ]);
  const [currentStep, setCurrentStep] = useState(1);

  // ✅ ÉTAT POUR AFFICHAGE TOUTES LES TAILLES
  const [showAllClothingSizes, setShowAllClothingSizes] = useState(false);
  const [showAllShoeSizes, setShowAllShoeSizes] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    birthDate: '',
    gender: 'UNISEX' as 'MALE' | 'FEMALE' | 'UNISEX',
    avatar_url: '',
    clothingSizes: [] as string[],
    shoeSizes: [] as string[],
    preferredBrands: [] as string[],
    favoriteColors: [] as string[],
    preferredStyles: [] as string[],
  });

  // ============================================================================
  // 🎯 HANDLERS
  // ============================================================================

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isStep1Valid =
      formData.name.trim() && (isParentOnboarding || formData.birthDate);
    if (isStep1Valid) {
      onSubmit({
        name: formData.name.trim(),
        birthDate: formData.birthDate,
        gender: formData.gender,
        avatar_url: formData.avatar_url,
        clothingSizes: formData.clothingSizes,
        shoeSizes: formData.shoeSizes,
        preferredBrands: formData.preferredBrands,
        favoriteColors: formData.favoriteColors,
        preferredStyles: formData.preferredStyles,
      });
    }
  };

  const handleNext = (e?: React.MouseEvent | React.FormEvent) => {
    e?.preventDefault();
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleStepClick = (step: number) => {
    const step1Valid =
      formData.name.trim() && (isParentOnboarding || formData.birthDate);
    if (step < currentStep || (step === currentStep + 1 && step1Valid)) {
      setCurrentStep(step);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      let fileToUpload = file;

      // ✅ CONVERSION HEIC AUTOMATIQUE
      if (
        file.name.toLowerCase().includes('.heic') ||
        file.name.toLowerCase().includes('.heif') ||
        file.type === ''
      ) {
        // Import dynamique pour éviter les erreurs de build
        const heic2any = (await import('heic2any')).default;

        try {
          const convertedBlob = (await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.8,
          })) as Blob;

          // Créer un nouveau fichier avec le bon nom
          fileToUpload = new File(
            [convertedBlob],
            file.name.replace(/\.heic$/i, '.jpg'),
            {
              type: 'image/jpeg',
            }
          );
        } catch (conversionError) {
          console.error('❌ ERREUR CONVERSION HEIC:', conversionError);
          showError(
            t(
              'common:errors.heicConversion',
              'Impossible de convertir cette image HEIC. Essayez avec une photo PNG ou JPG.'
            )
          );
          return;
        }
      }

      // ✅ Votre API backend (sécurisé + premium)
      const uploadData = new FormData();
      uploadData.append('file', fileToUpload);

      const response = await fetch(
        `${API_BASE_URL}/api/v1/upload?type=AVATAR`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('glowp_access_token')}`,
          },
          body: uploadData,
        }
      );

      if (!response.ok)
        throw new Error(
          `${t('common:errors.httpError', 'Erreur HTTP')}: ${response.status}`
        );
      const data = await response.json();

      // La réponse de votre API est différente, essayons plusieurs propriétés
      const avatarUrl =
        data.data?.url || data.url || data.secure_url || data.data?.secure_url;
      setFormData(prev => ({ ...prev, avatar_url: avatarUrl }));
    } catch (error) {
      console.error('💥 ERREUR UPLOAD:', error);
      showError(
        `${t('common:errors.uploadError', "Erreur lors de l'upload")}: ${error instanceof Error ? error.message : t('common:errors.unknownError', 'Erreur inconnue')}`
      );
    }
  };

  // ============================================================================
  // 🧮 CALCULS INTELLIGENTS - LOGIQUE PARFAITE CORRIGÉE
  // ============================================================================

  const ageData = formData.birthDate
    ? calculateChildAge(formData.birthDate)
    : null;
  const currentAge = ageData
    ? ageData.ageText.includes('mois') && !ageData.ageText.includes('ans')
      ? Math.max(1, Math.ceil(ageData.ageValue / 12))
      : ageData.ageValue
    : 0;
  const isAdultProfile = currentAge >= 18;

  // ✅ TAILLES INTELLIGENTES RECOMMANDÉES - LOGIQUE PARFAITE CORRIGÉE
  const getRecommendedClothingSizes = () => {
    if (isAdultProfile) return ['S', 'M', 'L'];

    // 🔧 CORRECTION CRITIQUE : Bien distinguer âge en mois vs âge en années
    const ageData_value = ageData ? ageData.ageValue : 0;

    // 🚨 CONDITION STRICTE : Si on calcule l'âge en mois, alors on est ≤ 36 mois
    if (
      ageData &&
      ageData.ageText.includes('mois') &&
      !ageData.ageText.includes('ans')
    ) {
      // ✅ ÂGE EN MOIS UNIQUEMENT (0-36 mois) : ageValue = mois
      const ageInMonths = ageData_value;
      if (ageInMonths <= 3) return ['3M', '6M', '9M'];
      if (ageInMonths <= 6) return ['6M', '9M', '12M'];
      if (ageInMonths <= 9) return ['9M', '12M', '18M'];
      if (ageInMonths <= 12) return ['12M', '18M', '24M'];
      if (ageInMonths <= 18) return ['18M', '24M', '3A'];
      if (ageInMonths <= 24) return ['24M', '3A', '4A'];
      if (ageInMonths <= 30) return ['24M', '3A', '4A']; // 25-30 mois
      if (ageInMonths <= 36) return ['3A', '4A', '5A']; // 31-36 mois
    } else {
      // ✅ ÂGE EN ANNÉES (3+ ans) : ageValue = années - JAMAIS DE MOIS !
      const ageInYears = ageData_value;
      if (ageInYears <= 4)
        return [`${ageInYears}A`, `${ageInYears + 1}A`, `${ageInYears + 2}A`];
      if (ageInYears <= 12)
        return [`${ageInYears}A`, `${ageInYears + 1}A`, `${ageInYears + 2}A`];

      // ✅ ADOLESCENTS 13-16 ans : Transition vers tailles adultes
      if (ageInYears <= 14) return ['12A', '14A', 'XS'];
      if (ageInYears <= 16) return ['16A', 'XS', 'S', 'M'];
      return ['14A', '15A', '16A'];
    }

    return ['3A', '4A', '5A']; // fallback
  };

  const getRecommendedShoeSizes = () => {
    if (isAdultProfile)
      return ['37', '38', '39', '40', '41', '42', '43', '44', '45'];

    // 🔧 CORRECTION CRITIQUE : Bien distinguer âge en mois vs âge en années
    const ageData_value = ageData ? ageData.ageValue : 0;

    // 🚨 CONDITION STRICTE : Si on calcule l'âge en mois, alors on est ≤ 36 mois
    if (
      ageData &&
      ageData.ageText.includes('mois') &&
      !ageData.ageText.includes('ans')
    ) {
      // ✅ ÂGE EN MOIS UNIQUEMENT (0-36 mois) : ageValue = mois
      const ageInMonths = ageData_value;
      if (ageInMonths <= 6) return ['17', '18', '19']; // 0-6 mois
      if (ageInMonths <= 12) return ['19', '20', '21']; // 6-12 mois
      if (ageInMonths <= 18) return ['21', '22', '23']; // 12-18 mois
      if (ageInMonths <= 24) return ['23', '24', '25']; // 18-24 mois
      if (ageInMonths <= 36) return ['25', '26', '27']; // 24-36 mois
    } else {
      // ✅ ÂGE EN ANNÉES (3+ ans) : ageValue = années
      const ageInYears = ageData_value;
      if (ageInYears <= 5) return ['27', '28', '29']; // 3-5 ans
      if (ageInYears <= 7) return ['29', '30', '31']; // 5-7 ans
      if (ageInYears <= 9) return ['31', '32', '33']; // 7-9 ans
      if (ageInYears <= 11) return ['33', '34', '35']; // 9-11 ans
      if (ageInYears <= 13) return ['35', '36', '37']; // 11-13 ans
      if (ageInYears <= 15) return ['37', '38', '39']; // 13-15 ans
      if (ageInYears <= 16) return ['39', '42', '45']; // 15-16 ans (grands pieds!)
    }

    return ['39', '40', '41']; // fallback
  };

  const clothingSizes = getRecommendedClothingSizes();
  const shoeSizes = getRecommendedShoeSizes();

  if (!isOpen) return null;

  const stepIcons = [User, Shirt, Palette, Camera];
  const stepTitles = [
    t('profiles-children:creation.basicInfo.title', 'Informations'),
    t('profiles-children:creation.sizes.title', 'Tailles'),
    t('preferences-common:title', 'Préférences'),
    t('profiles-children:creation.photo.title', 'Photo'),
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl w-full max-w-sm sm:max-w-lg mx-auto max-h-[98vh] sm:max-h-[95vh] overflow-hidden shadow-2xl border border-brand-border">
        {/* ============================================================================ */}
        {/* 🎨 HEADER MOBILE-FIRST PREMIUM */}
        {/* ============================================================================ */}

        <div className="p-3 sm:p-4 md:p-6 border-b bg-brand-offwhite border-brand-border">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center bg-brand-500">
                <Baby className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-brand-dark">
                {isParentOnboarding
                  ? t(
                      'profiles-children:creation.createParentProfile',
                      'Configurer mon profil'
                    )
                  : t(
                      'profiles-children:creation.createChildProfile',
                      'Créer un profil enfant'
                    )}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl transition-all duration-200 hover:scale-110 bg-brand-light"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-brand-500" />
            </button>
          </div>

          {/* Progress Bar Mobile-First */}
          <div className="space-y-3 sm:space-y-4">
            {/* Version mobile compacte */}
            <div className="block sm:hidden">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="font-medium text-brand-500">
                  {t('common:progress.step', 'Étape')} {currentStep}/4
                </span>
                <span className="text-brand-warm">
                  {stepTitles[currentStep - 1]}
                </span>
              </div>
              <div className="w-full bg-brand-border rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full transition-all duration-300 bg-brand-500"
                  style={{ width: `${(currentStep / 4) * 100}%` }}
                />
              </div>
            </div>

            {/* Version desktop complète */}
            <div className="hidden sm:flex items-center justify-between">
              {[1, 2, 3, 4].map((step, index) => {
                const Icon = stepIcons[index];
                const isActive = step <= currentStep;
                const isCurrent = step === currentStep;
                const isClickable =
                  step < currentStep ||
                  (step === currentStep + 1 &&
                    formData.name.trim() &&
                    formData.birthDate);

                return (
                  <div key={step} className="flex items-center flex-1">
                    <div className="flex flex-col items-center">
                      <button
                        type="button"
                        onClick={() => handleStepClick(step)}
                        disabled={!isClickable && step !== currentStep}
                        className={`w-9 h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${isCurrent ? 'scale-110 shadow-lg' : ''} ${isClickable ? 'cursor-pointer hover:scale-105' : step === currentStep ? 'cursor-default' : 'cursor-not-allowed opacity-50'}`}
                        style={{
                          backgroundColor: isActive
                            ? 'var(--brand-500)'
                            : '#e5e7eb',
                          color: isActive ? 'white' : '#6b7280',
                          boxShadow: isCurrent
                            ? '0 8px 25px rgba(200, 168, 130, 0.3)'
                            : 'none',
                        }}
                      >
                        <Icon className="w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                      </button>
                      <span
                        className={`text-xs lg:text-sm font-medium mt-1 sm:mt-2 text-center transition-colors duration-300 hidden md:block ${isActive ? 'text-brand-dark' : 'text-brand-warm'}`}
                      >
                        {stepTitles[index]}
                      </span>
                    </div>
                    {step < 4 && (
                      <div
                        className="flex-1 h-0.5 sm:h-1 mx-1 sm:mx-2 lg:mx-4 rounded-full transition-all duration-300"
                        style={{
                          backgroundColor:
                            step < currentStep ? 'var(--brand-500)' : '#e5e7eb',
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-3 sm:p-4 md:p-6 max-h-[70vh] sm:max-h-[60vh] overflow-y-auto">
            {/* ============================================================================ */}
            {/* 🎨 ÉTAPE 1: INFORMATIONS MOBILE-FIRST */}
            {/* ============================================================================ */}

            {currentStep === 1 && (
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <div className="text-center mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto mb-2 sm:mb-3 md:mb-4 rounded-xl sm:rounded-2xl flex items-center justify-center bg-brand-light">
                    <User className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-brand-500" />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-brand-dark mb-1 sm:mb-2">
                    {t(
                      'profiles-children:creation.basicInfo.title',
                      'Informations personnelles'
                    )}
                  </h3>
                  <p className="text-xs sm:text-sm text-brand-warm">
                    {t(
                      'profiles-children:creation.basicInfo.description',
                      'Commençons par les informations de base'
                    )}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-brand-dark">
                    <Heart className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                    {t(
                      'profiles-children:creation.basicInfo.childName',
                      "Nom de l'enfant"
                    )}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full p-2.5 sm:p-3 md:p-4 border rounded-lg sm:rounded-xl text-sm sm:text-base font-medium transition-all duration-200 focus:scale-[1.02] border-brand-border bg-white"
                    onFocus={e => {
                      e.currentTarget.style.borderColor = 'var(--brand-500)';
                      e.currentTarget.style.boxShadow =
                        '0 0 0 2px rgba(200, 168, 130, 0.1)';
                    }}
                    onBlur={e => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    placeholder={t(
                      'profiles-children:creation.basicInfo.namePlaceholder',
                      'Emma, Paul, Lucas...'
                    )}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-brand-dark">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                    {t(
                      'profiles-children:creation.basicInfo.birthDate',
                      'Date de naissance'
                    )}
                  </label>
                  <input
                    type="date"
                    value={formData.birthDate}
                    onChange={e =>
                      setFormData({ ...formData, birthDate: e.target.value })
                    }
                    className="w-full p-2.5 sm:p-3 md:p-4 border rounded-lg sm:rounded-xl text-sm sm:text-base font-medium transition-all duration-200 focus:scale-[1.02] border-brand-border bg-white"
                    onFocus={e => {
                      e.currentTarget.style.borderColor = 'var(--brand-500)';
                      e.currentTarget.style.boxShadow =
                        '0 0 0 2px rgba(200, 168, 130, 0.1)';
                    }}
                    onBlur={e => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    required={!isParentOnboarding}
                    disabled={isLoading}
                    max={new Date().toISOString().split('T')[0]}
                    min={
                      isParentOnboarding
                        ? new Date(Date.now() - 100 * 365 * 24 * 60 * 60 * 1000)
                            .toISOString()
                            .split('T')[0]
                        : new Date(Date.now() - 17 * 365 * 24 * 60 * 60 * 1000)
                            .toISOString()
                            .split('T')[0]
                    }
                  />
                  {formData.birthDate && (
                    <div className="p-2.5 sm:p-3 rounded-lg sm:rounded-xl border text-xs sm:text-sm bg-brand-offwhite border-brand-border">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                        <p className="font-medium text-brand-500">
                          {t(
                            'profiles-children:creation.basicInfo.calculatedAge',
                            'Âge calculé'
                          )}
                          : {calculateChildAge(formData.birthDate).ageText}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2 sm:space-y-3">
                  <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-brand-dark">
                    <Star className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                    {t('profiles-children:creation.basicInfo.gender', 'Genre')}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      {
                        key: 'UNISEX',
                        label: t(
                          'profiles-children:creation.basicInfo.genderUnisex',
                          'Mixte'
                        ),
                        icon: Heart,
                      },
                      {
                        key: 'MALE',
                        label: t(
                          'profiles-children:creation.basicInfo.genderMale',
                          'Garçon'
                        ),
                        icon: User,
                      },
                      {
                        key: 'FEMALE',
                        label: t(
                          'profiles-children:creation.basicInfo.genderFemale',
                          'Fille'
                        ),
                        icon: Star,
                      },
                    ].map(gender => {
                      const IconComponent = gender.icon;
                      return (
                        <button
                          key={gender.key}
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              gender: gender.key as any,
                            })
                          }
                          className={`p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl border text-xs sm:text-sm md:text-base font-semibold transition-all duration-200 hover:scale-105 ${formData.gender === gender.key ? 'scale-105 shadow-lg' : ''}`}
                          style={{
                            borderColor:
                              formData.gender === gender.key
                                ? 'var(--brand-500)'
                                : '#e5e7eb',
                            backgroundColor:
                              formData.gender === gender.key
                                ? '#f3f4f6'
                                : 'white',
                            color:
                              formData.gender === gender.key
                                ? 'var(--brand-500)'
                                : '#6b7280',
                            boxShadow:
                              formData.gender === gender.key
                                ? '0 8px 25px rgba(200, 168, 130, 0.2)'
                                : 'none',
                          }}
                          disabled={isLoading}
                        >
                          <div className="flex flex-col items-center gap-1 sm:gap-2">
                            <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                            <span className="text-xs sm:text-sm leading-tight">
                              {gender.label}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================================ */}
            {/* 👕 ÉTAPE 2: TAILLES ULTRA PREMIUM AVEC SELECT CUSTOM */}
            {/* ============================================================================ */}

            {currentStep === 2 && (
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <div className="text-center mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto mb-2 sm:mb-3 md:mb-4 rounded-xl sm:rounded-2xl flex items-center justify-center bg-brand-light">
                    <Shirt className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-brand-500" />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-brand-dark mb-1 sm:mb-2">
                    {t(
                      'profiles-children:creation.sizes.title',
                      'Tailles actuelles'
                    )}
                  </h3>
                  {formData.birthDate && (
                    <div className="inline-flex items-center gap-2 px-2 sm:px-3 md:px-4 py-1 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm bg-brand-light">
                      <Gift className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                      <span className="font-medium text-brand-500">
                        {t(
                          'profiles-children:creation.sizes.recommendedFor',
                          'Recommandées pour'
                        )}{' '}
                        {calculateChildAge(formData.birthDate).ageText}
                      </span>
                    </div>
                  )}
                </div>

                {/* ✅ TAILLES VÊTEMENTS AVEC SELECT PREMIUM */}
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-brand-dark">
                    <Shirt className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                    {t(
                      'profiles-children:creation.sizes.clothingSize',
                      'Tailles vêtements'
                    )}
                  </label>

                  <PremiumSelect
                    value=""
                    onSelect={value => {
                      if (value && !formData.clothingSizes.includes(value)) {
                        setFormData({
                          ...formData,
                          clothingSizes: [...formData.clothingSizes, value],
                        });
                      }
                    }}
                    options={(showAllClothingSizes
                      ? isAdultProfile
                        ? ADULT_CLOTHING_SIZES
                        : currentAge >= 13
                          ? [
                              ...ALL_CHILD_CLOTHING_SIZES,
                              ...ADULT_CLOTHING_SIZES,
                            ] // ✅ Ados : enfant + adulte
                          : ALL_CHILD_CLOTHING_SIZES
                      : clothingSizes
                    ).map(size => {
                      let label = size;
                      if (!isAdultProfile && showAllClothingSizes) {
                        // ✅ Affichage cohérent pour tailles enfants complètes
                        if (size.includes('M')) {
                          label = `${size.replace('M', '')} ${t('common:units.months', 'mois')}`;
                        } else if (size.includes('A')) {
                          const num = size.replace('A', '');
                          label = `${num} ${num === '1' ? t('common:units.year', 'an') : t('common:units.years', 'ans')}`;
                        }
                      } else if (!isAdultProfile && !showAllClothingSizes) {
                        // ✅ DESCRIPTIONS PARFAITES SELON L'ÂGE RÉEL - LOGIQUE UNIVERSELLE
                        const ageData_value = ageData ? ageData.ageValue : 0;
                        const isCurrentlyInMonths =
                          ageData &&
                          ageData.ageText.includes('mois') &&
                          !ageData.ageText.includes('ans');

                        if (size.includes('M')) {
                          const sizeMonths = parseInt(size.replace('M', ''));

                          const currentSizeLabel = t(
                            'sizes:currentSize',
                            'Taille actuelle'
                          );
                          const recommendedLabel = t(
                            'sizes:recommended',
                            'Recommandé'
                          );
                          const tooSmallLabel = t(
                            'sizes:tooSmall',
                            'Trop petit'
                          );
                          const monthsLabel = t('common:units.months', 'mois');

                          if (isCurrentlyInMonths) {
                            // Enfant actuellement en mois : logique normale
                            const currentAgeInMonths = ageData_value;
                            if (
                              Math.abs(sizeMonths - currentAgeInMonths) <= 6
                            ) {
                              label = `${size.replace('M', '')} ${monthsLabel} - ${currentSizeLabel}`;
                            } else {
                              label = `${size.replace('M', '')} ${monthsLabel} - ${recommendedLabel}`;
                            }
                          } else {
                            // Enfant en années : les tailles mois sont trop petites
                            label = `${size.replace('M', '')} ${monthsLabel} - ${tooSmallLabel}`;
                          }
                        } else if (size.includes('A')) {
                          const sizeYears = parseInt(size.replace('A', ''));
                          const yearLabel =
                            sizeYears === 1
                              ? t('common:units.year', 'an')
                              : t('common:units.years', 'ans');
                          const currentSizeLabel = t(
                            'sizes:currentSize',
                            'Taille actuelle'
                          );
                          const recommendedLabel = t(
                            'sizes:recommended',
                            'Recommandé'
                          );
                          const laterLabel = t('sizes:later', 'Plus tard');

                          if (isCurrentlyInMonths) {
                            // Enfant en mois : les tailles années sont pour plus tard
                            const currentAgeInYears = Math.floor(
                              ageData_value / 12
                            );
                            if (sizeYears <= currentAgeInYears + 2) {
                              label = `${sizeYears} ${yearLabel} - ${recommendedLabel}`;
                            } else {
                              label = `${sizeYears} ${yearLabel} - ${laterLabel}`;
                            }
                          } else {
                            // Enfant en années : logique normale
                            const currentAgeInYears = ageData_value;
                            if (
                              sizeYears <= currentAgeInYears + 1 &&
                              sizeYears >= currentAgeInYears
                            ) {
                              label = `${sizeYears} ${yearLabel} - ${currentSizeLabel}`;
                            } else {
                              label = `${sizeYears} ${yearLabel} - ${recommendedLabel}`;
                            }
                          }
                        } else {
                          // Tailles adultes pour ados
                          label = `${size} - ${t('sizes:recommended', 'Recommandé')}`;
                        }
                      }
                      return { value: size, label };
                    })}
                    placeholder={t(
                      'profiles-children:creation.sizes.selectClothingSize',
                      'Sélectionner une taille'
                    )}
                    disabled={isLoading}
                    icon={
                      <Shirt className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                    }
                  />

                  {/* ✅ BOUTON VOIR TOUTES LES TAILLES */}
                  {!showAllClothingSizes && (
                    <button
                      type="button"
                      onClick={() => setShowAllClothingSizes(true)}
                      className="w-full p-2.5 sm:p-3 border-2 border-dashed rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 hover:scale-[1.02] border-brand-border bg-brand-offwhite text-brand-500"
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = 'var(--brand-500)';
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>
                          {isAdultProfile
                            ? t(
                                'profiles-children:creation.sizes.showAllAdultSizes',
                                '🌍 Toutes les tailles internationales'
                              )
                            : t(
                                'profiles-children:creation.sizes.showAllChildSizes',
                                '📏 Choisir une autre taille (1M → 16A)'
                              )}
                        </span>
                      </div>
                    </button>
                  )}

                  {showAllClothingSizes && (
                    <button
                      type="button"
                      onClick={() => setShowAllClothingSizes(false)}
                      className="text-xs sm:text-sm font-medium transition-colors duration-200 text-brand-500"
                      onMouseEnter={e =>
                        (e.currentTarget.style.opacity = '0.7')
                      }
                      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                    >
                      ←{' '}
                      {t(
                        'profiles-children:creation.sizes.backToRecommended',
                        'Revenir aux recommandations'
                      )}
                    </button>
                  )}

                  {formData.clothingSizes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2 sm:mt-3">
                      {formData.clothingSizes.map((size, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-semibold border transition-all duration-200 hover:scale-105 bg-brand-light border-brand-border text-brand-500"
                        >
                          {size.includes('M')
                            ? `${size.replace('M', '')} ${t('common:units.months', 'mois')}`
                            : size.includes('A')
                              ? `${size.replace('A', '')} ${size.replace('A', '') === '1' ? t('common:units.year', 'an') : t('common:units.years', 'ans')}`
                              : size}
                          <button
                            type="button"
                            onClick={() =>
                              setFormData({
                                ...formData,
                                clothingSizes: formData.clothingSizes.filter(
                                  (_, i) => i !== index
                                ),
                              })
                            }
                            className="ml-1 sm:ml-1.5 hover:scale-110 transition-transform duration-200 text-brand-500"
                          >
                            <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* ✅ POINTURES AVEC SELECT PREMIUM */}
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-brand-dark">
                    <Gift className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                    {t(
                      'profiles-children:creation.sizes.shoeSize',
                      'Pointures'
                    )}
                  </label>

                  <PremiumSelect
                    value=""
                    onSelect={value => {
                      if (value && !formData.shoeSizes.includes(value)) {
                        setFormData({
                          ...formData,
                          shoeSizes: [...formData.shoeSizes, value],
                        });
                      }
                    }}
                    options={(showAllShoeSizes
                      ? isAdultProfile
                        ? ADULT_SHOE_SIZES
                        : currentAge >= 13
                          ? [...ALL_CHILD_SHOE_SIZES, ...ADULT_SHOE_SIZES] // ✅ Ados : enfant + adulte
                          : ALL_CHILD_SHOE_SIZES
                      : shoeSizes
                    ).map(size => {
                      let label = `${size} EU`;
                      if (!isAdultProfile && !showAllShoeSizes) {
                        label = `${size} EU - ${t('sizes:recommendedForAge', 'Recommandé pour cet âge')}`;
                      }
                      return { value: size, label };
                    })}
                    placeholder={t(
                      'profiles-children:creation.sizes.selectShoeSize',
                      'Sélectionner une pointure'
                    )}
                    disabled={isLoading}
                    icon={
                      <Gift className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                    }
                  />

                  {/* ✅ BOUTON VOIR TOUTES LES POINTURES */}
                  {!showAllShoeSizes && (
                    <button
                      type="button"
                      onClick={() => setShowAllShoeSizes(true)}
                      className="w-full p-2.5 sm:p-3 border-2 border-dashed rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 hover:scale-[1.02] border-brand-border bg-brand-offwhite text-brand-500"
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = 'var(--brand-500)';
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>
                          {isAdultProfile
                            ? t(
                                'profiles-children:creation.sizes.showAllAdultShoes',
                                '👠 Toutes les pointures internationales'
                              )
                            : t(
                                'profiles-children:creation.sizes.showAllChildShoes',
                                '👟 Choisir une autre pointure (15 → 40)'
                              )}
                        </span>
                      </div>
                    </button>
                  )}

                  {showAllShoeSizes && (
                    <button
                      type="button"
                      onClick={() => setShowAllShoeSizes(false)}
                      className="text-xs sm:text-sm font-medium transition-colors duration-200 text-brand-500"
                      onMouseEnter={e =>
                        (e.currentTarget.style.opacity = '0.7')
                      }
                      onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                    >
                      ←{' '}
                      {t(
                        'profiles-children:creation.sizes.backToRecommended',
                        'Revenir aux recommandations'
                      )}
                    </button>
                  )}

                  {formData.shoeSizes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2 sm:mt-3">
                      {formData.shoeSizes.map((size, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-semibold border transition-all duration-200 hover:scale-105 bg-brand-light border-brand-border text-brand-500"
                        >
                          {size} EU
                          <button
                            type="button"
                            onClick={() =>
                              setFormData({
                                ...formData,
                                shoeSizes: formData.shoeSizes.filter(
                                  (_, i) => i !== index
                                ),
                              })
                            }
                            className="ml-1 sm:ml-1.5 hover:scale-110 transition-transform duration-200 text-brand-500"
                          >
                            <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* ✅ CONSEIL PREMIUM */}
                  <div className="p-3 sm:p-4 rounded-xl border bg-brand-offwhite border-brand-border">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-brand-light">
                        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-brand-500">
                          {t(
                            'profiles-children:creation.sizes.growthTip',
                            'Conseil croissance'
                          )}
                        </p>
                        <p className="text-xs sm:text-sm text-brand-warm mt-1">
                          {t(
                            'profiles-children:creation.sizes.growthTipDescription',
                            'Les pieds des enfants grandissent vite ! Privilégiez le confort et vérifiez régulièrement.'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================================ */}
            {/* 🎨 ÉTAPE 3: PRÉFÉRENCES */}
            {/* ============================================================================ */}

            {currentStep === 3 && (
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <div className="text-center mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto mb-2 sm:mb-3 md:mb-4 rounded-xl sm:rounded-2xl flex items-center justify-center bg-brand-light">
                    <Palette className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-brand-500" />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-brand-dark mb-1 sm:mb-2">
                    {t('preferences-common:title', 'Préférences de style')}
                  </h3>
                  <p className="text-xs sm:text-sm text-brand-warm">
                    {t(
                      'preferences-common:description',
                      'Aidez-nous à trouver des articles qui plairont à votre enfant'
                    )}
                  </p>
                </div>

                <AutocompletePreferences
                  type="brand"
                  selectedItems={formData.preferredBrands}
                  onAdd={brand =>
                    setFormData(prev => ({
                      ...prev,
                      preferredBrands: [...prev.preferredBrands, brand],
                    }))
                  }
                  onRemove={brand =>
                    setFormData(prev => ({
                      ...prev,
                      preferredBrands: prev.preferredBrands.filter(
                        b => b !== brand
                      ),
                    }))
                  }
                  placeholder={t(
                    'preferences-common:brands.placeholder',
                    'Rechercher une marque... (ex: Zara, Nike, Disney)'
                  )}
                />

                <AutocompletePreferences
                  type="color"
                  selectedItems={formData.favoriteColors}
                  onAdd={color =>
                    setFormData(prev => ({
                      ...prev,
                      favoriteColors: [...prev.favoriteColors, color],
                    }))
                  }
                  onRemove={color =>
                    setFormData(prev => ({
                      ...prev,
                      favoriteColors: prev.favoriteColors.filter(
                        c => c !== color
                      ),
                    }))
                  }
                  placeholder={t(
                    'preferences-common:colors.placeholder',
                    'Rechercher une couleur... (ex: Rose, Bleu, Arc-en-ciel)'
                  )}
                />

                <AutocompletePreferences
                  type="style"
                  selectedItems={formData.preferredStyles}
                  onAdd={style =>
                    setFormData(prev => ({
                      ...prev,
                      preferredStyles: [...prev.preferredStyles, style],
                    }))
                  }
                  onRemove={style =>
                    setFormData(prev => ({
                      ...prev,
                      preferredStyles: prev.preferredStyles.filter(
                        s => s !== style
                      ),
                    }))
                  }
                  placeholder={t(
                    'preferences-common:styles.placeholder',
                    'Rechercher un style... (ex: Princesse, Sport, Kawaii)'
                  )}
                />

                <div className="p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl border text-center bg-brand-offwhite border-brand-border">
                  <Heart className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 mx-auto mb-2 sm:mb-3 text-brand-500" />
                  <p className="text-xs sm:text-sm font-medium text-brand-500">
                    {t(
                      'preferences-common:optional',
                      'Ces préférences sont optionnelles mais nous aident à personnaliser les recommandations !'
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* ============================================================================ */}
            {/* 📸 ÉTAPE 4: PHOTO + RÉSUMÉ */}
            {/* ============================================================================ */}

            {currentStep === 4 && (
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <div className="text-center mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto mb-2 sm:mb-3 md:mb-4 rounded-xl sm:rounded-2xl flex items-center justify-center bg-brand-light">
                    <Camera className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-brand-500" />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-brand-dark mb-1 sm:mb-2">
                    {t(
                      'profiles-children:creation.photo.title',
                      'Photo de profil'
                    )}
                  </h3>
                  <p className="text-xs sm:text-sm text-brand-warm">
                    {t(
                      'profiles-children:creation.photo.description',
                      'Ajoutez une photo pour personnaliser le profil'
                    )}
                  </p>
                </div>

                <div className="text-center">
                  <div className="mb-4 sm:mb-6">
                    {formData.avatar_url ? (
                      <div className="relative inline-block">
                        <Avatar
                          src={formData.avatar_url}
                          alt={t(
                            'profiles-children:creation.photo.preview',
                            'Aperçu'
                          )}
                          name={
                            formData.name ||
                            t(
                              'profiles-children:creation.photo.previewFallback',
                              'Aperçu'
                            )
                          }
                          size="xl"
                          className="mx-auto border-4 w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 shadow-[0_20px_40px_rgba(200,168,130,0.3)] border-[#e5e7eb] border-solid"
                        />
                        <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center border-2 sm:border-4 border-white bg-brand-500">
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 rounded-full mx-auto flex items-center justify-center border-4 border-dashed transition-all duration-300 hover:scale-105 border-brand-border bg-brand-offwhite">
                        {formData.name ? (
                          <div className="text-center">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-full mx-auto mb-1 flex items-center justify-center bg-brand-500">
                              <span className="text-white text-sm sm:text-lg md:text-2xl font-bold">
                                {formData.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="text-xs font-medium text-brand-500">
                              {formData.name}
                            </span>
                          </div>
                        ) : (
                          <Camera className="w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 text-brand-border" />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 sm:space-y-4">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={isLoading}
                      />
                      <div
                        className="inline-flex items-center gap-2 px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 md:py-4 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold transition-all duration-200 hover:scale-105 border bg-brand-500 border-brand-500 text-white"
                        style={{
                          boxShadow: '0 8px 25px rgba(200, 168, 130, 0.3)',
                        }}
                      >
                        <Upload className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                        <span>
                          {t(
                            'profiles-children:creation.photo.uploadPhoto',
                            'Choisir une photo'
                          )}
                        </span>
                      </div>
                    </label>
                    <p className="text-xs sm:text-sm text-brand-warm">
                      {t(
                        'profiles-children:creation.photo.optional',
                        'Optionnel - Vous pouvez ajouter une photo plus tard'
                      )}
                    </p>
                  </div>
                </div>

                {/* ✅ RÉSUMÉ MOBILE-FIRST AVEC N18I PARFAIT */}
                <div className="p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl border bg-brand-offwhite border-brand-border">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 text-brand-500" />
                    <h4 className="text-sm sm:text-base font-bold text-brand-dark">
                      {t(
                        'profiles-children:creation.summary.title',
                        'Résumé du profil'
                      )}
                    </h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs sm:text-sm font-medium text-brand-warm">
                        {t(
                          'profiles-children:creation.basicInfo.childName',
                          'Nom'
                        )}
                        :
                      </span>
                      <span className="text-xs sm:text-sm font-bold text-brand-500">
                        {formData.name}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs sm:text-sm font-medium text-brand-warm">
                        {t('profiles-children:creation.basicInfo.age', 'Âge')}:
                      </span>
                      <span className="text-xs sm:text-sm font-bold text-brand-500">
                        {formData.birthDate
                          ? calculateChildAge(formData.birthDate).ageText
                          : `${currentAge} ${t('common:units.years', 'ans')}`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs sm:text-sm font-medium text-brand-warm">
                        {t(
                          'profiles-children:creation.basicInfo.gender',
                          'Genre'
                        )}
                        :
                      </span>
                      <span className="text-xs sm:text-sm font-bold text-brand-500">
                        {formData.gender === 'MALE'
                          ? t(
                              'profiles-children:creation.basicInfo.genderMale',
                              'Garçon'
                            )
                          : formData.gender === 'FEMALE'
                            ? t(
                                'profiles-children:creation.basicInfo.genderFemale',
                                'Fille'
                              )
                            : t(
                                'profiles-children:creation.basicInfo.genderUnisex',
                                'Mixte'
                              )}
                      </span>
                    </div>
                    {formData.clothingSizes.length > 0 && (
                      <div className="flex justify-between items-start">
                        <span className="text-xs sm:text-sm font-medium text-brand-warm">
                          {t(
                            'profiles-children:creation.sizes.clothingSize',
                            'Tailles'
                          )}
                          :
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-right text-brand-500">
                          {formData.clothingSizes.join(', ')}
                        </span>
                      </div>
                    )}
                    {formData.shoeSizes.length > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs sm:text-sm font-medium text-brand-warm">
                          {t(
                            'profiles-children:creation.sizes.shoeSize',
                            'Pointures'
                          )}
                          :
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-brand-500">
                          {formData.shoeSizes.join(', ')} EU
                        </span>
                      </div>
                    )}
                    {(formData.preferredBrands.length > 0 ||
                      formData.favoriteColors.length > 0 ||
                      formData.preferredStyles.length > 0) && (
                      <div className="flex justify-between items-start">
                        <span className="text-xs sm:text-sm font-medium text-brand-warm">
                          {t('preferences-common:summary', 'Préférences')}:
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-right text-brand-500">
                          {[
                            ...formData.preferredStyles,
                            ...formData.favoriteColors,
                            ...formData.preferredBrands,
                          ]
                            .slice(0, 3)
                            .join(', ')}
                          {[
                            ...formData.preferredStyles,
                            ...formData.favoriteColors,
                            ...formData.preferredBrands,
                          ].length > 3 && t('common:more', '...')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ============================================================================ */}
          {/* 🚀 FOOTER MOBILE-FIRST PARFAIT */}
          {/* ============================================================================ */}

          <div className="p-3 sm:p-4 md:p-6 border-t flex flex-col-reverse sm:flex-row justify-between gap-2 sm:gap-3 md:gap-4 bg-brand-offwhite border-brand-border">
            <div className="flex items-center">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold transition-all duration-200 hover:scale-105 bg-brand-light text-brand-500"
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor = '#e5e7eb')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = '#f3f4f6')
                  }
                  disabled={isLoading}
                >
                  <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{t('common:buttons.previous', 'Précédent')}</span>
                </button>
              )}
            </div>

            <div className="flex gap-2 sm:gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 sm:flex-none px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 border rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold transition-all duration-200 hover:scale-105 border-brand-border bg-white text-brand-warm"
                onMouseEnter={e =>
                  (e.currentTarget.style.backgroundColor = '#f9fafb')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.backgroundColor = 'white')
                }
                disabled={isLoading}
              >
                {isParentOnboarding
                  ? t('common:buttons.later', 'Compléter plus tard')
                  : t('common:buttons.cancel', 'Annuler')}
              </button>

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    handleNext(e);
                  }}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-white rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold transition-all duration-200 hover:scale-105 bg-brand-500"
                  style={{ boxShadow: '0 4px 14px rgba(200, 168, 130, 0.3)' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#b8966f';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(200, 168, 130, 0.4)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'var(--brand-500)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 14px rgba(200, 168, 130, 0.3)';
                  }}
                  disabled={isLoading}
                >
                  <span>{t('common:buttons.next', 'Suivant')}</span>
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 text-white rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 bg-brand-500"
                  style={{ boxShadow: '0 4px 14px rgba(200, 168, 130, 0.3)' }}
                  onMouseEnter={e =>
                    !isLoading &&
                    (e.currentTarget.style.backgroundColor = '#b8966f')
                  }
                  onMouseLeave={e =>
                    !isLoading &&
                    (e.currentTarget.style.backgroundColor = 'var(--brand-500)')
                  }
                  disabled={
                    isLoading ||
                    !formData.name.trim() ||
                    (!isParentOnboarding && !formData.birthDate)
                  }
                >
                  {isLoading ? (
                    <>
                      <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{t('common:status.creating', 'Création...')}</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>{t('common:buttons.create', 'Créer')}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProfileModal;
